import { PublicFooter, PublicHeader } from "@/components/PublicMarketingLayout";

const sections = [
  {
    title: "About these terms",
    body: [
      "These Terms of Use explain how you may use Alchemy Blueprint Engine, a planning and blueprint-generation service operated by Godswill Arum, trading as AI Traffic Alchemy.",
      "By using the service, you agree to these terms. If you do not agree, you should not use the service.",
    ],
  },
  {
    title: "Who may use the service",
    body: [
      "You must be at least 18 years old to use the service.",
      "You must provide accurate account information and use the service only for lawful purposes.",
    ],
  },
  {
    title: "Account registration and security",
    body: [
      "Authentication is provided by Clerk. You are responsible for keeping your login credentials and account access secure.",
      "You must not share your account with unauthorised users or attempt to access another user's account, projects or blueprints.",
    ],
  },
  {
    title: "Description of the service",
    body: [
      "Alchemy Blueprint Engine helps users turn app ideas into product specifications, screen plans, data models, acceptance criteria, edge cases, test scenarios and implementation prompts.",
      "The software is a planning and blueprint-generation tool. It does not guarantee that an app will work, be secure, be commercially successful or comply with all laws.",
    ],
  },
  {
    title: "Free, Standard and Pro access",
    body: [
      "The service may offer Free, Standard and Pro access levels with different blueprint limits, output depth, export options and tools.",
      "Current plan details are shown on the pricing page. We may update plan features for future purchases, but we will not deliberately remove paid access you have already purchased without a valid reason.",
    ],
  },
  {
    title: "Purchases, subscriptions and billing",
    body: [
      "Direct in-app purchases and subscriptions are processed through Stripe. Stripe handles payment-card details.",
      "Pro subscriptions may be cancelled at any time and remain active until the end of the current paid billing period.",
      "One-time purchases are non-recurring.",
    ],
  },
  {
    title: "WarriorPlus and third-party purchases",
    body: [
      "Some purchases may be made through WarriorPlus or another approved purchase channel.",
      "Third-party purchase flows may have their own checkout, confirmation, refund and support processes. We use purchase events from those channels to grant or update access in the application.",
    ],
  },
  {
    title: "Refunds and cancellations",
    body: [
      "Refunds are provided where required by applicable law, where the service is faulty or materially misdescribed, or in accordance with the purchase-channel terms.",
      "WarriorPlus purchases are also subject to the refund process and terms shown through WarriorPlus.",
      "Cancelling a Pro subscription stops future renewals but does not normally remove access before the end of the current paid billing period.",
    ],
  },
  {
    title: "Acceptable use",
    body: [
      "You must not use the service for unlawful, harmful, abusive, infringing, deceptive or security-compromising activity.",
      "You must not submit API keys, passwords, confidential client information, unauthorised personal data, trade secrets or content you do not have permission to use.",
      "You must not attempt to reverse engineer, overload, scrape, bypass access controls or interfere with the service.",
    ],
  },
  {
    title: "User-submitted content",
    body: [
      "You retain ownership of your original app ideas and content submitted to the service.",
      "You grant us the licence needed to host, process, transmit, analyse and transform your submissions to provide the service, generate outputs, maintain your account and support the product.",
      "You are responsible for ensuring you have the rights and permissions needed for anything you submit.",
    ],
  },
  {
    title: "AI-generated content and limitations",
    body: [
      "The service uses AI to generate blueprints and related output. AI output may be incomplete, inaccurate, outdated, unsuitable or inconsistent.",
      "You remain responsible for reviewing generated specifications, code, data handling, security, accessibility, legal compliance and deployment decisions.",
      "You may use generated blueprints for your own app planning and build work, subject to third-party rights, purchase-channel terms and applicable law. We do not claim ownership of your app ideas.",
    ],
  },
  {
    title: "Intellectual property",
    body: [
      "We own or license the Alchemy Blueprint Engine software, branding, user interface, workflows, templates and platform content, except for your submitted content and generated outputs allocated to you under these terms.",
      "You must not copy, resell or reproduce the platform itself except as allowed by these terms.",
    ],
  },
  {
    title: "Licence to use the service",
    body: [
      "We grant you a limited, revocable, non-exclusive, non-transferable licence to use the service for your own planning and blueprint-generation purposes according to your access level.",
    ],
  },
  {
    title: "Availability, maintenance and changes",
    body: [
      "We aim to keep the service available, but we do not guarantee uninterrupted access.",
      "The service may be updated, suspended or temporarily unavailable for maintenance, security, provider outages or operational reasons.",
    ],
  },
  {
    title: "Third-party services",
    body: [
      "The service depends on third-party providers including Clerk, Stripe, WarriorPlus, GoHighLevel, OpenAI, hosting and database providers.",
      "We are not responsible for third-party outages, policy changes or account restrictions outside our reasonable control.",
    ],
  },
  {
    title: "Suspension and termination",
    body: [
      "We may suspend or terminate access if you breach these terms, misuse the service, create risk for other users, fail to pay required fees or if required by law.",
      "You may stop using the service at any time. Some purchase, entitlement and accounting records may be retained as described in the Privacy Policy.",
    ],
  },
  {
    title: "Disclaimers",
    body: [
      "The service is provided on an as-is and as-available basis to the fullest extent permitted by law.",
      "We do not promise that generated blueprints will be error-free, suitable for every use case, legally compliant, secure, profitable or accepted by any specific AI builder.",
    ],
  },
  {
    title: "Limitation of liability",
    body: [
      "Nothing in these terms limits liability that cannot legally be limited, including liability for death or personal injury caused by negligence, fraud or your mandatory consumer rights.",
      "To the fullest extent permitted by law, we are not liable for indirect losses, loss of profit, loss of revenue, loss of data, business interruption or decisions you make based on generated content.",
    ],
  },
  {
    title: "Indemnity",
    body: [
      "If you use the service for business purposes, you agree to reimburse us for reasonable losses, costs or claims arising from your unlawful use, infringing submissions, breach of these terms or misuse of generated output, where legally enforceable.",
    ],
  },
  {
    title: "Consumer rights",
    body: [
      "Nothing in these terms removes statutory consumer rights that apply to you.",
      "If a local consumer law gives you mandatory protections, those protections continue to apply.",
    ],
  },
  {
    title: "Governing law and jurisdiction",
    body: [
      "These terms are governed by the laws of England and Wales.",
      "The courts of England and Wales have jurisdiction, subject to any mandatory consumer rights that apply in your country of residence.",
    ],
  },
  {
    title: "Changes to these terms",
    body: [
      "We may update these terms as the service changes. The updated version will be posted on this page with a revised date.",
    ],
  },
  {
    title: "Contact details",
    body: [
      "Godswill Arum, trading as AI Traffic Alchemy.",
      "30 Charles Street, Reading, RG1 7DB, United Kingdom.",
      "support@aitrafficalchemy.com.",
    ],
  },
];

export function TermsOfUse() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-white via-indigo-50/40 to-white text-slate-950">
      <PublicHeader />
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">
          Legal
        </p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-950">
          Terms of Use
        </h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          Last updated: 4 July 2026
        </p>
        <div className="mt-8 space-y-6 rounded-[2rem] border border-indigo-100 bg-white p-5 shadow-sm sm:p-8">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-bold text-slate-950">{section.title}</h2>
              <div className="mt-3 space-y-3 text-sm leading-7 text-slate-600">
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
